/**
 * useCauseReportExport - Export mutation and polling hook
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  CauseReportExportRequest,
  ExportStatus,
  UseCauseReportExportReturn,
} from '../types';
import { EXPORT_TIMEOUT } from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../utils';

interface ExportApiRequest {
  format: string;
  filters: Record<string, unknown>;
  include_sections: Record<string, boolean>;
  metadata?: Record<string, string>;
}

interface ExportApiResponse {
  export_id: string;
  status: string;
  progress?: number;
  download_url?: string;
  expires_at?: string;
  error?: string;
}

async function initiateExport(
  datasetId: string,
  request: CauseReportExportRequest
): Promise<ExportApiResponse> {
  const apiRequest: ExportApiRequest = {
    format: request.format,
    filters: mapFiltersToApiParams(request.filters),
    include_sections: {
      overview: request.includeSections.overview,
      categories: request.includeSections.categories,
      top_causes: request.includeSections.topCauses,
      outcomes: request.includeSections.outcomes,
      dimensions: request.includeSections.dimensions,
      high_risk: request.includeSections.highRisk,
    },
    metadata: request.metadata,
  };

  return fetchWithAuth(
    `/v1/datasets/${datasetId}/cause-report/export`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(apiRequest),
    }
  );
}

async function checkExportStatus(
  datasetId: string,
  exportId: string
): Promise<ExportApiResponse> {
  return fetchWithAuth(
    `/v1/datasets/${datasetId}/cause-report/export/${exportId}/status`
  );
}

/** Initial poll delay - short so we catch early progress updates */
const INITIAL_POLL_DELAY = 500; // 500ms

/** Maximum polling interval (cap for exponential backoff) */
const MAX_POLL_INTERVAL = 10000; // 10 seconds

/** Backoff multiplier for exponential polling */
const BACKOFF_MULTIPLIER = 1.5;

export function useCauseReportExport(): UseCauseReportExportReturn {
  const [status, setStatus] = useState<ExportStatus>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentExportRef = useRef<{ datasetId: string; exportId: string } | null>(null);
  const currentIntervalRef = useRef<number>(INITIAL_POLL_DELAY);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    currentIntervalRef.current = INITIAL_POLL_DELAY;
  }, []);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  // Poll for export status with exponential backoff
  const startPolling = useCallback((datasetId: string, exportId: string) => {
    currentExportRef.current = { datasetId, exportId };
    currentIntervalRef.current = INITIAL_POLL_DELAY;

    // Set timeout
    timeoutRef.current = setTimeout(() => {
      cleanup();
      setStatus('error');
      setError('Export timed out. Please try again.');
    }, EXPORT_TIMEOUT);

    // Poll with exponential backoff
    const poll = async () => {
      try {
        const response = await checkExportStatus(datasetId, exportId);

        if (response.status === 'ready') {
          cleanup();
          setProgress(100);
          setStatus('ready');
          setDownloadUrl(response.download_url);
          return;
        } else if (response.status === 'error') {
          cleanup();
          setStatus('error');
          setError(response.error || 'Export failed');
          return;
        }

        setProgress(response.progress ?? 0);

        // Continue polling with exponential backoff
        currentIntervalRef.current = Math.min(
          currentIntervalRef.current * BACKOFF_MULTIPLIER,
          MAX_POLL_INTERVAL
        );
        pollTimeoutRef.current = setTimeout(poll, currentIntervalRef.current);
      } catch {
        cleanup();
        setStatus('error');
        setError('Failed to check export status');
      }
    };

    // Start first poll quickly, then ramp up with backoff
    pollTimeoutRef.current = setTimeout(poll, currentIntervalRef.current);
  }, [cleanup]);

  // Export mutation
  const mutation = useMutation({
    mutationFn: (request: CauseReportExportRequest) =>
      initiateExport(request.datasetId, request),
    onMutate: () => {
      setStatus('generating');
      setProgress(0);
      setDownloadUrl(undefined);
      setError(undefined);
    },
    onSuccess: (response, request) => {
      if (response.status === 'ready') {
        setStatus('ready');
        setDownloadUrl(response.download_url);
      } else if (response.status === 'error') {
        setStatus('error');
        setError(response.error);
      } else {
        // Set initial progress from the POST response
        setProgress(response.progress ?? 0);
        // Start polling for status
        startPolling(request.datasetId, response.export_id);
      }
    },
    onError: (err: Error) => {
      cleanup();
      setStatus('error');
      setError(err.message || 'Failed to initiate export');
    },
  });

  const reset = useCallback(() => {
    cleanup();
    setStatus('idle');
    setProgress(0);
    setDownloadUrl(undefined);
    setError(undefined);
    currentExportRef.current = null;
  }, [cleanup]);

  return {
    exportReport: mutation.mutate,
    status,
    progress,
    downloadUrl,
    error,
    reset,
    isExporting: status === 'generating',
  };
}
